// Maps decimal fractions to their HTML entity equivalents.
// Covers all standard US culinary fractions.
const FRACTIONS: { value: number; html: string }[] = [
  { value: 1 / 8, html: "&frac18;" },
  { value: 1 / 4, html: "&frac14;" },
  { value: 1 / 3, html: "&#8531;" },
  { value: 3 / 8, html: "&frac38;" },
  { value: 1 / 2, html: "&frac12;" },
  { value: 5 / 8, html: "&frac58;" },
  { value: 2 / 3, html: "&#8532;" },
  { value: 3 / 4, html: "&frac34;" },
  { value: 7 / 8, html: "&frac78;" },
];

const TOLERANCE = 0.07;

/**
 * Converts a decimal ingredient amount to a human-readable string using
 * HTML fraction entities (e.g. 0.25 → "&frac14;", 2.5 → "2 &frac12;").
 *
 * The fractional part is always snapped to the nearest standard culinary
 * fraction within ±TOLERANCE. Whole numbers with no fractional part are
 * returned as plain integers (e.g. 2.0 → "2").
 *
 * The returned string may contain HTML entities and must be rendered via
 * dangerouslySetInnerHTML.
 */
export function formatAmount(amount: number): string {
  if (!Number.isFinite(amount) || amount <= 0) return "";

  const whole = Math.floor(amount);
  const decimal = amount - whole;

  // Find the nearest fraction within tolerance
  let bestFraction: (typeof FRACTIONS)[0] | null = null;
  let bestDiff = TOLERANCE;

  for (const frac of FRACTIONS) {
    const diff = Math.abs(decimal - frac.value);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestFraction = frac;
    }
  }

  if (bestFraction) {
    return whole > 0 ? `${whole} ${bestFraction.html}` : bestFraction.html;
  }

  // No fraction matched — return whole number or plain decimal
  return whole > 0 && decimal < TOLERANCE ? `${whole}` : `${amount}`;
}
